import React from 'react';

/**
 * OptimizedImage Component
 * 
 * Renders images with automatic WebP fallback support using the <picture> element.
 * If the image has a WebP URL and a fallback URL, it will serve WebP to browsers
 * that support it, and fall back to JPEG/PNG for older browsers.
 * 
 * Usage:
 * <OptimizedImage 
 *   src="/uploads/images/abc123.webp"
 *   fallbackSrc="/uploads/images/abc123_fallback.jpg"
 *   alt="My image"
 *   className="w-full h-auto"
 * />
 * 
 * Or with a media object from the API:
 * <OptimizedImage 
 *   src={media.url}
 *   fallbackSrc={media.fallback_url}
 *   alt={media.alt_text}
 * />
 */
export function OptimizedImage({ 
  src, 
  fallbackSrc, 
  alt = '', 
  className = '', 
  style = {},
  loading = 'lazy',
  ...props 
}) {
  // If no fallback provided, just render a regular img
  if (!fallbackSrc) {
    return (
      <img 
        src={src} 
        alt={alt} 
        className={className}
        style={style}
        loading={loading}
        {...props}
      />
    );
  }

  // Determine MIME types from file extensions
  const getMediaType = (url) => {
    if (!url) return null;
    if (url.endsWith('.webp')) return 'image/webp';
    if (url.endsWith('.jpg') || url.endsWith('.jpeg')) return 'image/jpeg';
    if (url.endsWith('.png')) return 'image/png';
    if (url.endsWith('.gif')) return 'image/gif';
    return null;
  };

  const srcType = getMediaType(src);
  const fallbackType = getMediaType(fallbackSrc);

  // Use <picture> element for automatic format selection
  return (
    <picture>
      {/* WebP source - browsers that support it will use this */}
      {srcType === 'image/webp' && (
        <source srcSet={src} type="image/webp" />
      )}
      
      {/* Fallback source for other formats */}
      {fallbackType && fallbackType !== srcType && (
        <source srcSet={fallbackSrc} type={fallbackType} />
      )}
      
      {/* Fallback img element - always required in <picture> */}
      <img 
        src={fallbackSrc || src} 
        alt={alt} 
        className={className}
        style={style}
        loading={loading}
        {...props}
      />
    </picture>
  );
}

/**
 * Helper function to get optimized image props from a media object
 * 
 * Usage:
 * const imageProps = getOptimizedImageProps(mediaFile);
 * <OptimizedImage {...imageProps} className="w-full" />
 */
export function getOptimizedImageProps(media) {
  if (!media) return { src: '', alt: '' };
  
  return {
    src: media.url,
    fallbackSrc: media.fallback_url || null,
    alt: media.alt_text || media.original_filename || ''
  };
}

export default OptimizedImage;
